import { useEffect, useState } from "react";
import "./ManualAdjustmentsUploadModal.styles.scss";

// MUI Imports
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Package Imports
import Uppy from "@uppy/core";
import { Dashboard } from "@uppy/react";
import "@uppy/core/dist/style.min.css";
import "@uppy/dashboard/dist/style.min.css";
import Papa from "papaparse";
import { v4 as uuidv4 } from "uuid";

// Type Imports
import { IManualAdjustmentDto } from "@pulse-ui/codegen/generated_ts/org_entity";

// Util Imports
import { uploadManualAdjustment } from "@/utils/promises";

interface ISnackbarAlertMessage {
  id: string;
  message: string;
  status: number;
}

const CustomDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const ManualAdjustmentsUploadModal = ({
  handleClose,
  setLoading,
  handleFetchActiveManualAdjustments,
  portfolio_id_param,
  dropcopy_feed_type_param,
}: {
  handleClose: () => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  handleFetchActiveManualAdjustments: (
    location_origin: string,
    portfolio_id_param: string,
    dropcopy_feed_type_param: string
  ) => Promise<{ status: number; message: any }>;
  portfolio_id_param: string;
  dropcopy_feed_type_param: string;
}) => {
  // State - START -------------------------------------
  const location_origin = window.location.origin;
  const [uppy] = useState(
    () =>
      new Uppy({
        restrictions: { maxNumberOfFiles: 1, allowedFileTypes: [".csv"] },
        autoProceed: true,
      })
  );
  const [manualAdjustmentsPayload, setManualAdjustmentsPayload] = useState<
    IManualAdjustmentDto[]
  >([]);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  // Error Handling / Alert Message States
  const [alertMessages, setAlertMessages] = useState<ISnackbarAlertMessage[]>(
    []
  );
  // State - END ---------------------------------------

  // Methods - START -----------------------------------
  /**
   * Function to push an alert message.
   * This function checks if the alert message already exists in the state.
   * If it does not exist, it adds the alert message to the state.
   *
   * @param {IAlertMessage} alert - The alert message to push.
   */
  const pushAlert = (alert: ISnackbarAlertMessage) => {
    const existing_alerts: ISnackbarAlertMessage[] = alertMessages.filter(
      (state_alert) => alert.message == state_alert.message
    );

    if (existing_alerts.length === 0) {
      setAlertMessages([...alertMessages, alert]);
    }
  };

  /**
   * Function to close an alert message.
   * This function removes the alert message with the specified ID from the state.
   *
   * @param {string} id - The ID of the alert message to close.
   */
  const closeAlertMessage = (id: string) => {
    setAlertMessages((prevState) => {
      return prevState.filter((alert) => alert.id !== id);
    });
  };

  const processCsvData = (
    csvData: string
  ): { data: IManualAdjustmentDto[]; errors: string[] } => {
    const parsedData: IManualAdjustmentDto[] = [];
    const errors: string[] = [];

    Papa.parse(csvData, {
      header: true,
      skipEmptyLines: true,
      complete: (results: any) => {
        results.data.forEach((row: any) => {
          let metadata: any = {};
          let record: any = {};

          for (let key in row) {
            let [type, field] = key.split(".");
            let value = row[key];

            try {
              if (type === "metadata") {
                metadata[field] = value;
              } else if (type === "record") {
                if (value.startsWith("[{") && value.endsWith("}]")) {
                  record[field] = JSON.parse(value);
                } else {
                  record[field] = value;
                }
              }
            } catch (error: any) {
              const errorMessage = `Error parsing field "${key}": ${error.message}`;
              errors.push(errorMessage);
              pushAlert({
                id: uuidv4(),
                message: errorMessage,
                status: 500,
              });
            }
          }

          const manualAdjustmentDto: any = {
            metadata,
            record,
          };

          parsedData.push(manualAdjustmentDto);
        });
      },
      error: (error: any) => {
        const errorMessage = `Error parsing CSV data: ${error.message}`;
        errors.push(errorMessage);
        pushAlert({
          id: uuidv4(),
          message: errorMessage,
          status: 500,
        });
      },
    });

    return { data: parsedData, errors };
  };

  const handleSubmitManualAdjustment = async () => {
    setLoading(true);
    try {
      const upload_manual_adjustment_res = await uploadManualAdjustment(
        location_origin,
        manualAdjustmentsPayload
      );

      pushAlert({
        id: uuidv4(),
        message: "Suceessfully uploaded manual adjustments.",
        status: upload_manual_adjustment_res.status,
      });

      await handleFetchActiveManualAdjustments(
        location_origin,
        portfolio_id_param,
        dropcopy_feed_type_param
      );
    } catch (error_res: any) {
      pushAlert({
        id: uuidv4(),
        message: error_res.error.error,
        status: error_res?.status || 500,
      });
    } finally {
      setManualAdjustmentsPayload([]);
      setIsSubmitEnabled(false);
      handleClose();
      setLoading(false);
    }
  };

  const cleanCsvFile = (csv: string) => {
    let cleanedCsvString = csv;

    // Handle double and single smart quotes
    cleanedCsvString = cleanedCsvString
      .replace(/[“”]/g, '"')
      .replace(/[‘’]/g, "'");

    return cleanedCsvString;
  };
  // Methods - END -------------------------------------

  // useEffects - START --------------------------------
  useEffect(() => {
    uppy.addPostProcessor(async (fileIDs) => {
      for (const fileID of fileIDs) {
        const file = uppy.getFile(fileID);

        uppy.emit("postprocess-progress", file, {
          mode: "indeterminate",
          message: `Processing CSV data (${file.name})`,
        });

        await new Promise((resolve, reject) => {
          try {
            const reader = new FileReader();
            reader.onload = (event) => {
              const csvData = event.target?.result as string;
              const cleanedCsvData = cleanCsvFile(csvData);

              const parsedData = processCsvData(cleanedCsvData);

              if (parsedData.errors.length > 0) {
                pushAlert({
                  id: uuidv4(),
                  message: `${parsedData.errors.join(", ")}`,
                  status: 500,
                });
                reject();
              }

              setManualAdjustmentsPayload(parsedData.data);
              setIsSubmitEnabled(true);
              resolve(200);
            };
            reader.onerror = (error) => {
              reject(error);
            };
            reader.readAsText(file.data as Blob);
          } catch (error) {
            reject(error);
          }
        });
      }

      return Promise.resolve("Processed CSV data");
    });

    uppy.on("file-added", (file) => {
      const reader = new FileReader();
      reader.readAsText(file.data as Blob);
    });

    uppy.on("upload-error", (file, error) => {
      pushAlert({
        id: file?.name || uuidv4(),
        message: error.message,
        status: 500,
      });
    });

    uppy.on("postprocess-complete", (file) => {
      pushAlert({
        id: file?.name || uuidv4(),
        message: "File has been parsed successfully. Submit to upload.",
        status: 200,
      });
    });

    return () => {
      uppy.destroy();
    };
  }, [uppy]);
  // useEffects - END ----------------------------------

  return (
    <div className="apikeys-modal">
      <CustomDialog
        onClose={handleClose}
        open={true}
        className="apikeys-modal-dialog"
        maxWidth="xl"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            boxShadow: "0px 0px 4px #777",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} className="apikeys-modal-dialog-title">
          Upload Manual Adjustments File
        </DialogTitle>
        <DialogContent dividers className="apikeys-modal-dialog-content">
          <Dashboard
            uppy={uppy}
            width="100%"
            height={400}
            note="Upload a CSV file"
            showProgressDetails
            showSelectedFiles
            hideUploadButton
          />
        </DialogContent>
        <DialogActions className="apikeys-modal-dialog-actions">
          <Button
            onClick={handleClose}
            className="apikeys-modal-dialog-actions-button"
            sx={{
              color: "bg.300",
              typography: "fontSizeSmall",
              "&:disabled": {
                color: "bg.100",
              },
            }}
          >
            Close
          </Button>

          <Button
            onClick={handleSubmitManualAdjustment}
            className="apikeys-modal-dialog-actions-button"
            disabled={!isSubmitEnabled}
            sx={{
              color: "bg.700",
              typography: "fontSizeSmall",
              "&:disabled": {
                color: "bg.200",
              },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </CustomDialog>

      {alertMessages.map((alertMessage: ISnackbarAlertMessage) => {
        return (
          <Snackbar
            key={alertMessage.id}
            open={true}
            onClose={() => closeAlertMessage(alertMessage.id)}
          >
            <Alert
              onClose={() => closeAlertMessage(alertMessage.id)}
              severity={alertMessage.status === 200 ? "success" : "error"}
              variant="filled"
              sx={{ width: "100%" }}
            >
              {alertMessage.message}
            </Alert>
          </Snackbar>
        );
      })}
    </div>
  );
};

export default ManualAdjustmentsUploadModal;
